/**
 * InlineProposalPanel — hosts the proposal surface as a REAL CHILD of the
 * chat page's layout (a flex sibling of the chat column), driven by the same
 * URL-synced slideout stack the global portal renderer uses.
 *
 * Mount this on a page that has claimed the `proposal` kind via
 * `registerInlineHost(['proposal'])` — the global `SlideoutRenderer` stands
 * down for claimed kinds, so exactly one surface renders the proposal.
 *
 * There is no `SlideoutMenu` here, so this component IS the chrome
 * (`InlineCrmObjectPanel` precedent): it publishes `SurfaceDismissContext`
 * and renders `SlideoutMenu.Header` (which carries the close button) around
 * the same header/body regions the portaled `ProposalSidebar` uses — one
 * definition of the proposal surface, two hosts.
 *
 * Close clears the stack IMMEDIATELY (like `InlineCrmObjectPanel`), not via
 * `closeProposal()` — that path defers the stack clear to coordinate the
 * overlay's 500ms exit animation, which a docked pane doesn't have; using it
 * here would leave the pane lingering after the close click.
 */

import { useQueryClient } from '@tanstack/react-query';
import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
} from 'react';

import { BuilderPanel } from '@/components/reasoning/BuilderPanel';
import { DockedSidePanel } from '@/components/reasoning/DockedSidePanel';
import { useProposalSidebar } from '@/contexts/ActionSidebarContext';
import { useOrg } from '@/contexts/OrgContext';
import { ProposalSurfaceProvider } from '@/contexts/ProposalSurfaceContext';
import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';
import {
  SurfaceDismissContext,
  type SurfaceDismissContextValue,
} from '@/lib/base/overlays/surface-dismiss-context';
import {
  type SlideoutEntry,
  useSlideoutStackContext,
} from '@/lib/crm/contexts/SlideoutStackContext';
import type { BuilderDraft } from '@/lib/crm/hooks/useBuilderSubject.helpers';
import { useSlideoutStack } from '@/lib/crm/hooks/useSlideoutStack';
import type { PreviewOverlay } from '@/lib/crm/preview';
import type { ProposalDetail } from '@/lib/proposals/core/types';
import { useProposalOptional } from '@/lib/proposals/hooks/useProposal';
import { useReverseAssociationTypeResolver } from '@/lib/proposals/hooks/useReverseAssociationTypeResolver';
import { proposalQueryKeys } from '@/lib/proposals/query/keys';
import { derivePreviewOverlays } from '@/lib/proposals/utils/derivePreviewOverlays';
import { nextProposalId } from '@/lib/proposals/utils/nextProposalId';
import { soleSubjectEntityOf } from '@/lib/proposals/utils/soleSubjectEntity';

import ProposalDetailPanel from './ProposalDetailPanel';
import {
  PROPOSAL_HEADER_CLASS,
  ProposalSidebarErrorBody,
  ProposalSidebarHeader,
  ProposalSidebarHeaderActions,
  ProposalSidebarLoadingBody,
  type ProposalSidebarState,
  ProposalSidebarStateProvider,
  type ProposalSidebarValue,
} from './ProposalSidebarRegions';

// Static slot elements — the regions read everything from context.
const HEADER_SLOT = <ProposalSidebarHeader />;
const HEADER_ACTIONS_SLOT = <ProposalSidebarHeaderActions />;

const SELECT_ALL = () => true;
const EMPTY_OVERLAYS: Record<string, PreviewOverlay> = {};

export interface InlineProposalPanelProps {
  /** Stack entry this panel renders. Must be `kind: 'proposal'`. */
  entry: SlideoutEntry;
  /**
   * Width in px — owned by the host (ReasoningChat's shared slideout width),
   * mirroring how `BuilderRecordPanelDesktop` is sized.
   */
  width: number;
  /**
   * Called after the entity-record variant's Save/Cancel resolves the
   * proposal, so the host chat can re-read the conversation (the cards'
   * resolution chips read off the conversation payload).
   */
  onApproved?: () => void;
  onRejected?: () => void;
  /**
   * Chrome slot for the host's `EdgeResizeHandle` on the chat-facing edge —
   * forwarded to `DockedSidePanel`, which is the positioned ancestor the
   * handle anchors to.
   */
  resizeHandle?: ReactNode;
}

export function InlineProposalPanel({
  entry,
  width,
  onApproved,
  onRejected,
  resizeHandle,
}: InlineProposalPanelProps): ReactElement | null {
  const proposalId = useMemo(() => {
    const parsed = Number(entry.id);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [entry.id]);

  const { currentOrg } = useOrg();
  const queryClient = useQueryClient();
  const { clearStack } = useSlideoutStack();
  const { registerPushGuard, unregisterPushGuard } = useSlideoutStackContext();
  const { contextProposalIds } = useProposalSidebar();

  const handleClose = useCallback(() => {
    clearStack();
  }, [clearStack]);

  // Propagate the auto-save guard from ProposalDetailPanel up to the
  // slideout stack context, exactly as ProposalSidebar does.
  const handleGuardChange = useCallback(
    (guard: ((action: () => void) => boolean) | null) => {
      if (guard) {
        registerPushGuard(guard);
      } else {
        unregisterPushGuard();
      }
    },
    [registerPushGuard, unregisterPushGuard]
  );

  // Clean up the push guard when this panel unmounts (user closed it, the
  // host lost eligibility, or another kind was pushed on top). Effect Event
  // rather than a dep: re-running the effect on an unstable context identity
  // would unregister a live guard mid-session.
  const unregisterOnUnmount = useEffectEvent(() => {
    unregisterPushGuard();
  });
  useEffect(() => {
    return () => {
      unregisterOnUnmount();
    };
    // unregisterOnUnmount is an Effect Event: always sees the latest
    // unregisterPushGuard without being a reactive dependency
    // (REACT_19_PATTERNS.md § 4.1).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    data: action,
    error,
    refetch,
  } = useProposalOptional(
    currentOrg?.id ?? 0,
    currentOrg?.id ? (proposalId ?? undefined) : undefined
  );

  const retry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const handleActionUpdate = useCallback(
    (updatedAction: ProposalDetail) => {
      if (currentOrg?.id && proposalId) {
        queryClient.setQueryData(
          proposalQueryKeys.detail(currentOrg.id, proposalId),
          updatedAction
        );
      }
    },
    [currentOrg?.id, proposalId, queryClient]
  );

  // Same next-proposal lookup as ProposalSidebar.getNextActionId — one shared
  // definition, so the docked pane and the overlay can never disagree.
  const getNextActionId = useCallback(
    (currentId: number): number | null =>
      nextProposalId(contextProposalIds, currentId),
    [contextProposalIds]
  );

  const handleActionComplete = useCallback(() => {
    const completedId = proposalId;
    handleClose();
    // Cache refresh mirrors ProposalSidebar.handleActionComplete — see the
    // long comment there for why detail refetches eagerly while lists is
    // `refetchType: 'none'` (the asymmetry is deliberate; do not tidy it).
    if (currentOrg?.id && completedId) {
      void queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.detail(currentOrg.id, completedId),
      });
      void queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.lists(currentOrg.id),
        refetchType: 'none',
      });
    }
  }, [proposalId, handleClose, currentOrg?.id, queryClient]);

  // ── Entity-record variant ────────────────────────────────────────────────
  // A proposal that is JUST creating or updating a single entity renders as
  // that entity's record — the builder-mode treatment: preview overlays over
  // the record surface, Save-bar approve — instead of the generic proposal
  // panel. Multi-entity / multi-kind proposals, and rejected ones (whose
  // create never ran, so there is no record to show), keep the proposal
  // panel.
  const resolver = useReverseAssociationTypeResolver(currentOrg?.id);
  const detail = action as ProposalDetail | undefined;
  const subject = useMemo(
    () => (detail ? soleSubjectEntityOf(detail.actions) : undefined),
    [detail]
  );
  const isPending = detail?.resolution === 'pending';
  const entityOverlays = useMemo(
    () =>
      detail && isPending && subject
        ? derivePreviewOverlays(detail.actions, SELECT_ALL, resolver)
        : EMPTY_OVERLAYS,
    [detail, isPending, subject, resolver]
  );
  const subjectOverlay = subject ? entityOverlays[subject.entityId] : undefined;
  const entityDraft = useMemo<BuilderDraft | undefined>(
    () =>
      isPending && detail && subjectOverlay
        ? { proposalId: detail.id, overlay: subjectOverlay }
        : undefined,
    [isPending, detail, subjectOverlay]
  );
  const showEntityVariant =
    subject !== undefined &&
    detail !== undefined &&
    (isPending || detail.resolution === 'approved');
  // Before approval of a create there is no saved row — the overlay IS the
  // record; afterwards the minted id names the real one.
  const savedEntityId =
    subjectOverlay?.kind === 'create' && isPending
      ? undefined
      : subject?.entityId;

  // One resolution state for the whole surface — the chrome's header exists
  // in every state, so a failed fetch renders as a retryable error instead of
  // a header-less dead pane.
  const state = useMemo<ProposalSidebarState>(() => {
    if (action) {
      return { status: 'ready', action: action as ProposalDetail };
    }
    if (error) {
      return { status: 'error', error, retry };
    }
    return { status: 'loading' };
  }, [action, error, retry]);

  const surfaceValue = useMemo<ProposalSidebarValue>(
    () => ({ state, close: handleClose }),
    [state, handleClose]
  );

  const dismiss = useMemo<SurfaceDismissContextValue>(
    () => ({
      close: handleClose,
      back: undefined,
      isEntering: false,
      isExiting: false,
    }),
    [handleClose]
  );

  if (!currentOrg?.id || entry.kind !== 'proposal') {
    return null;
  }

  if (showEntityVariant && subject) {
    return (
      <DockedSidePanel
        width={width}
        data-component='inline-proposal-panel'
        data-instance='entity-record'
      >
        <BuilderPanel
          orgId={currentOrg.id}
          entityTypeName={subject.entityTypeName}
          entityId={savedEntityId}
          overlays={entityOverlays}
          draft={entityDraft}
          onApproved={onApproved}
          onRejected={onRejected}
          onClose={handleClose}
        />
      </DockedSidePanel>
    );
  }

  let body: ReactNode;
  switch (state.status) {
    case 'loading':
      body = <ProposalSidebarLoadingBody />;
      break;
    case 'error':
      body = (
        <ProposalSidebarErrorBody error={state.error} retry={state.retry} />
      );
      break;
    case 'ready':
      body = (
        <ProposalDetailPanel
          key={state.action.id}
          action={state.action}
          onClose={handleClose}
          onUpdate={handleActionUpdate}
          onComplete={handleActionComplete}
          getNextActionId={getNextActionId}
          // The chrome renders the header row (ProposalSidebarHeader) so it
          // exists in every state, not only once the proposal has loaded.
          hideHeader
          // This pane lives INSIDE a conversation — the main chat is the way
          // to talk to the agent about this proposal; a second input here
          // would split the thread.
          hideChat
          onGuardChange={handleGuardChange}
        />
      );
      break;
    default:
      body = assertNever(state);
  }

  return (
    <DockedSidePanel
      width={width}
      resizeHandle={resizeHandle}
      data-component='inline-proposal-panel'
    >
      <ProposalSidebarStateProvider value={surfaceValue}>
        <SurfaceDismissContext value={dismiss}>
          <SlideoutMenu.Header
            className={PROPOSAL_HEADER_CLASS}
            actions={HEADER_ACTIONS_SLOT}
          >
            {HEADER_SLOT}
          </SlideoutMenu.Header>
          <SlideoutMenu.Content className='px-0 md:px-0'>
            <ProposalSurfaceProvider surface='sidebar'>
              {body}
            </ProposalSurfaceProvider>
          </SlideoutMenu.Content>
        </SurfaceDismissContext>
      </ProposalSidebarStateProvider>
    </DockedSidePanel>
  );
}

function assertNever(state: never): never {
  throw new Error(`Unhandled proposal panel state: ${JSON.stringify(state)}`);
}
